(() => {
  'use strict';

  const CARD_SELECTOR = '.jj-product-card';
  const SELECT_SELECTOR = '[data-jj-variant-select]';
  const TOGGLE_SELECTOR = '[data-jj-variant-toggle]';
  const MENU_SELECTOR = '[data-jj-variant-menu]';
  const OPTION_SELECTOR = '[data-jj-variant-option]';
  const ADD_SELECTOR = '.jj-card__add';

  function hasVariants(card) {
    const button = card.querySelector(ADD_SELECTOR);
    return button?.dataset.hasVariants === 'true';
  }

  function variantChosen(card) {
    return card.classList.contains('jj-card--variant-chosen');
  }

  function setButtonState(card, option) {
    const button = card.querySelector(ADD_SELECTOR);
    const text = button?.querySelector('[data-jj-add-text]');
    const control = card.querySelector('[data-jj-action-control]');
    if (!button || !text || !option) return;

    const available = option.dataset.available === 'true';
    const addText = button.dataset.addToCartText || 'Add to bag';
    const chooseText = button.dataset.chooseOptionText || 'Choose option';
    const soldOutText = card.dataset.soldOutText || 'Sold out';
    const needsChoice = hasVariants(card) && !variantChosen(card);

    control?.classList.toggle('is-sold-out', !needsChoice && !available);

    if (needsChoice) {
      button.type = 'button';
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      text.textContent = chooseText;
      return;
    }

    button.type = available ? 'submit' : 'button';
    button.disabled = !available;

    if (available) {
      button.removeAttribute('aria-disabled');
      text.textContent = addText;
    } else {
      button.setAttribute('aria-disabled', 'true');
      text.textContent = soldOutText;
    }
  }

  function setPrice(card, option) {
    const current = card.querySelector('[data-jj-current-price]');
    const compare = card.querySelector('[data-jj-compare-price]');
    if (current) current.textContent = option.dataset.price || '';
    if (compare) {
      const comparePrice = option.dataset.comparePrice || '';
      compare.textContent = comparePrice;
      compare.classList.toggle('is-hidden', !comparePrice);
    }
  }

  function setLinks(card, variantId) {
    card.querySelectorAll('[data-jj-product-link], .image-cont > a.product-link').forEach((link) => {
      try {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('variant', variantId);
        link.href = `${url.pathname}${url.search}${url.hash}`;
      } catch (e) {
        // Leave non-standard app URLs untouched.
      }
    });
  }

  function setWishlistVariant(card, option) {
    const variantId = option.value;
    const inStock = option.dataset.available === 'true' ? 'true' : 'false';
    card.querySelectorAll('.xb-wishlist__add, .xb-wishlist__remove').forEach((button) => {
      button.setAttribute('xb-product-variant', variantId);
      button.setAttribute('xb-is-in-stock', inStock);
    });
  }

  function setImageFromRenderedMedia(card, mediaId) {
    if (!mediaId) return false;
    const images = Array.from(card.querySelectorAll('.product-block__image'));
    const targetIndex = images.findIndex((image) => image.dataset.mediaId === String(mediaId));
    if (targetIndex < 0) return false;

    images.forEach((image, index) => {
      image.classList.remove('product-block__image--inactivated');
      image.classList.toggle('product-block__image--active', index === targetIndex);
      image.classList.toggle('product-block__image--show-on-hover', images.length > 1 && index === ((targetIndex + 1) % images.length));
    });

    const dots = card.querySelectorAll('.product-block__image-dot');
    dots.forEach((dot, index) => dot.classList.toggle('product-block__image-dot--active', index === targetIndex));
    return true;
  }

  function setImageFallback(card, option) {
    const imageUrl = option.dataset.image900 || option.dataset.image720 || option.dataset.image540 || option.dataset.image360;
    if (!imageUrl) return;

    const activeWrap = card.querySelector('.product-block__image--active') || card.querySelector('.product-block__image--primary');
    const img = activeWrap?.querySelector('img');
    if (!img) return;

    const srcset = [
      [option.dataset.image360, '360w'],
      [option.dataset.image540, '540w'],
      [option.dataset.image720, '720w'],
      [option.dataset.image900, '900w']
    ].filter(([url]) => url).map(([url, width]) => `${url} ${width}`).join(', ');

    img.src = imageUrl;
    if (srcset) img.srcset = srcset;
    if (option.dataset.mediaId) activeWrap.dataset.mediaId = option.dataset.mediaId;
  }

  function setVariantImage(card, option, userInitiated = true) {
    const mediaId = option.dataset.mediaId;
    const usedRenderedMedia = setImageFromRenderedMedia(card, mediaId);
    if (!usedRenderedMedia) setImageFallback(card, option);
    if (userInitiated && mediaId) card.classList.add('jj-card--variant-selected');
  }

  function updateMenuState(card, variantId) {
    card.querySelectorAll(OPTION_SELECTOR).forEach((button) => {
      const selected = button.dataset.variantId === String(variantId);
      button.classList.toggle('is-current', selected);
      button.setAttribute('aria-selected', selected ? 'true' : 'false');
    });
  }

  function syncVariant(card, select, userInitiated = true) {
    const option = select?.options?.[select.selectedIndex];
    if (!option || !option.value) return;

    const variantId = option.value;
    card.dataset.selectedVariantId = variantId;
    card.dataset.soldOutText = card.dataset.soldOutText || 'Sold out';

    setPrice(card, option);
    setButtonState(card, option);
    setLinks(card, variantId);
    setWishlistVariant(card, option);
    setVariantImage(card, option, userInitiated);
    updateMenuState(card, variantId);

    if (userInitiated) {
      card.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          variantId,
          productId: card.dataset.productId,
          source: 'product-card'
        }
      }));
    }
  }

  function setExpanded(card, expanded) {
    const control = card?.querySelector('[data-jj-action-control]');
    const menu = card?.querySelector(MENU_SELECTOR);
    if (!control || !menu) return;

    control.classList.toggle('is-picker-open', expanded);
    card.classList.toggle('jj-card--picker-open', expanded);
    menu.setAttribute('aria-hidden', expanded ? 'false' : 'true');
    card.querySelector(ADD_SELECTOR)?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    card.querySelector(TOGGLE_SELECTOR)?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  function closePicker(card) {
    setExpanded(card, false);
  }

  function closeOtherPickers(currentCard) {
    document.querySelectorAll(`${CARD_SELECTOR}.jj-card--picker-open`).forEach((card) => {
      if (card !== currentCard) closePicker(card);
    });
  }

  function openPicker(card) {
    if (!card || !hasVariants(card)) return;
    closeOtherPickers(card);
    setExpanded(card, true);

    const menu = card.querySelector(MENU_SELECTOR);
    const selected = menu?.querySelector(`${OPTION_SELECTOR}.is-current:not(:disabled)`);
    const firstAvailable = menu?.querySelector(`${OPTION_SELECTOR}:not(:disabled)`);
    const focusTarget = selected || firstAvailable;

    window.requestAnimationFrame(() => {
      focusTarget?.focus({ preventScroll: true });
    });
  }

  function togglePicker(card) {
    const control = card?.querySelector('[data-jj-action-control]');
    if (!control) return;
    if (control.classList.contains('is-picker-open')) closePicker(card);
    else openPicker(card);
  }

  function chooseVariant(card, optionButton) {
    const select = card?.querySelector(SELECT_SELECTOR);
    const variantId = optionButton?.dataset.variantId;
    if (!select || !variantId || optionButton.disabled) return;

    select.value = variantId;
    card.classList.add('jj-card--variant-chosen');

    // Mirror a real select interaction so review/cart/wishlist/analytics apps listening to the
    // native form control continue to receive the expected events. Our change listener performs
    // the visual sync and closes the menu.
    select.dispatchEvent(new Event('input', { bubbles: true }));
    select.dispatchEvent(new Event('change', { bubbles: true }));

    // Return focus to the primary CTA so keyboard/touch users have a predictable next action.
    window.requestAnimationFrame(() => card.querySelector(ADD_SELECTOR)?.focus({ preventScroll: true }));
  }

  document.addEventListener('change', (event) => {
    const select = event.target.closest?.(SELECT_SELECTOR);
    if (!select) return;
    const card = select.closest(CARD_SELECTOR);
    if (!card || !select.value) return;

    card.classList.add('jj-card--variant-chosen');
    syncVariant(card, select, true);
    closePicker(card);
  });

  document.addEventListener('click', (event) => {
    const variantOption = event.target.closest?.(OPTION_SELECTOR);
    if (variantOption) {
      event.preventDefault();
      event.stopPropagation();
      chooseVariant(variantOption.closest(CARD_SELECTOR), variantOption);
      return;
    }

    const toggle = event.target.closest?.(TOGGLE_SELECTOR);
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      togglePicker(toggle.closest(CARD_SELECTOR));
      return;
    }

    const addButton = event.target.closest?.(ADD_SELECTOR);
    if (addButton) {
      const card = addButton.closest(CARD_SELECTOR);
      if (card && hasVariants(card) && !variantChosen(card)) {
        event.preventDefault();
        event.stopPropagation();
        openPicker(card);
        return;
      }
      if (card?.classList.contains('jj-card--picker-open')) closePicker(card);
    }

    document.querySelectorAll(`${CARD_SELECTOR}.jj-card--picker-open`).forEach((card) => {
      if (!card.contains(event.target)) closePicker(card);
    });
  });

  document.addEventListener('keydown', (event) => {
    const optionButton = event.target.closest?.(OPTION_SELECTOR);
    if (optionButton) {
      const card = optionButton.closest(CARD_SELECTOR);
      const menu = optionButton.closest(MENU_SELECTOR);
      const enabledOptions = Array.from(menu?.querySelectorAll(`${OPTION_SELECTOR}:not(:disabled)`) || []);
      const index = enabledOptions.indexOf(optionButton);

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        const next = enabledOptions[(index + delta + enabledOptions.length) % enabledOptions.length];
        next?.focus({ preventScroll: true });
        next?.scrollIntoView({ block: 'nearest' });
        return;
      }

      if (event.key === 'Home') {
        event.preventDefault();
        enabledOptions[0]?.focus({ preventScroll: true });
        return;
      }

      if (event.key === 'End') {
        event.preventDefault();
        enabledOptions[enabledOptions.length - 1]?.focus({ preventScroll: true });
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        closePicker(card);
        card?.querySelector(ADD_SELECTOR)?.focus({ preventScroll: true });
        return;
      }
    }

    if (event.key === 'Escape') {
      document.querySelectorAll(`${CARD_SELECTOR}.jj-card--picker-open`).forEach((card) => closePicker(card));
    }
  });

  function initialize(root = document) {
    root.querySelectorAll?.(`${CARD_SELECTOR} ${SELECT_SELECTOR}`).forEach((select) => {
      const card = select.closest(CARD_SELECTOR);
      if (card) {
        card.classList.remove('jj-card--variant-chosen', 'jj-card--picker-open');
        syncVariant(card, select, false);
        closePicker(card);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
